// Client-side router — real URLs, a working Back button, and INV-1: a route
// change performs ZERO network calls. This module never calls fetch. It only
// ever reads `location.pathname` / pushes history state and notifies
// listeners; every page renders from the in-memory store (store.ts), which is
// hydrated once at startup and kept current by the WebSocket delta stream in
// later phases — never re-fetched on navigation.
import { signal } from '@preact/signals';

export interface Route {
  path: string;
}

// Addresses that moved (owner, 2026-10-06): "move reports and Money under
// company as a sub menu item" and "move HR and the SPC as a menu item in
// People". Every old link — a bookmark, a Quick Docs pin (src/favorites.js
// still hands out `/money#deposits`, `/reports#report.<name>`,
// `/company#compliance`), a link in an old email — lands on the new place
// instead of a dead page. Money's own hashes (`deposits`, `petty`, `other`)
// and Reports' (`report.<name>`) are carried across unchanged; Company
// understands them (pages/Company.tsx hashToCompany).
const MONEY_HASHES = new Set(['deposits', 'petty', 'other']);

/** Where an old address lives now, as `path#hash`, or null if it has not
 * moved. Pure — reads nothing, writes nothing. */
export function legacyRedirect(pathname: string, hash: string): string | null {
  const h = (hash || '').replace(/^#/, '');
  if (pathname === '/money') return `/company#${MONEY_HASHES.has(h) ? h : 'money'}`;
  if (pathname === '/reports') return `/company#${h.startsWith('report.') ? h : 'reports'}`;
  if (pathname === '/company') {
    if (h === 'hr') return '/people#hr';
    if (h === 'compliance' || h === 'service-compliance' || h === 'spc') return '/people#spc';
  }
  return null;
}

function splitUrl(url: string): { pathname: string; hash: string } {
  const at = url.indexOf('#');
  const before = at === -1 ? url : url.slice(0, at);
  const hash = at === -1 ? '' : url.slice(at);
  return { pathname: before.split('?')[0] || '/', hash };
}

function currentPath(): string {
  const path = window.location.pathname || '/';
  const moved = legacyRedirect(path, window.location.hash);
  if (moved) {
    // Replace, not push: Back must not return to an address that only bounces.
    window.history.replaceState({}, '', moved);
    return splitUrl(moved).pathname;
  }
  return path;
}

export const currentRoute = signal<Route>({ path: currentPath() });

/** Navigate to a path. Pushes real browser history. Makes no network calls. */
export function navigate(path: string): void {
  const parts = splitUrl(path);
  const moved = legacyRedirect(parts.pathname, parts.hash);
  if (moved) {
    pushAndAnnounce(moved);
    return;
  }
  if (currentPath() === path) return;
  // The route is the path alone; a query or hash stays in the address for the
  // page to read (Cursor review #22 — "/recruiting?candidate=123" used to
  // become the route, and no page is registered under that).
  pushAndAnnounce(path);
}

/** Push `url` and set the route. pushState never fires hashchange, so when
 * the page stays the same and only the hash moves (Company open, a link to
 * /money lands on /company#money), the mounted page is told. */
function pushAndAnnounce(url: string): void {
  const beforePath = window.location.pathname;
  const beforeHash = window.location.hash;
  window.history.pushState({}, '', url);
  currentRoute.value = { path: splitUrl(url).pathname };
  if (window.location.pathname === beforePath && window.location.hash !== beforeHash) {
    window.dispatchEvent(new Event('hashchange'));
  }
}

/** Swap the current address for another page's without a new history entry
 * — a page that finds itself opened on a moved hash (Company's old `#hr` /
 * `#compliance`, set after the route by a Quick Docs pin) hands off with
 * this. Makes no network calls. */
export function replaceRoute(url: string): void {
  window.history.replaceState({}, '', url);
  currentRoute.value = { path: splitUrl(url).pathname };
}

/**
 * Follow an in-app link written as an href ("/recruiting?tab=guide",
 * "/company#communications") — What's New links (owner, 2026-10-06). Pages
 * read their query/hash when they mount, so landing on a different page
 * opens the right tab; the route itself is the path alone, as everywhere.
 */
export function navigateHref(href: string): void {
  const u = new URL(href, window.location.origin);
  if (u.origin !== window.location.origin) { window.location.assign(href); return; }
  // A link to a moved page (/money, /reports, /company#hr …) lands where it
  // lives now — lib/router.ts legacyRedirect, same as navigate().
  pushAndAnnounce(legacyRedirect(u.pathname, u.hash) ?? u.pathname + u.search + u.hash);
  window.scrollTo(0, 0);
}

/** Click handler for server-written HTML (a What's New body): an in-app
 * `<a href="/...">` opens in place instead of reloading the whole site. */
export function followInAppLink(e: MouseEvent): void {
  if (e.defaultPrevented || e.button !== 0 || e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) return;
  const a = (e.target as Element | null)?.closest?.('a');
  const href = a?.getAttribute('href');
  if (!a || !href || !href.startsWith('/') || href.startsWith('//') || a.getAttribute('target')) return;
  e.preventDefault();
  navigateHref(href);
}

let popstateBound = false;

/** Wire up Back/Forward button support. Idempotent — safe to call once at startup. */
export function initRouter(): void {
  if (popstateBound) return;
  popstateBound = true;
  window.addEventListener('popstate', () => {
    currentRoute.value = { path: currentPath() };
  });
}

/** Test-only: reset router wiring between test files. */
export function __resetRouterForTests(): void {
  popstateBound = false;
}
